import { useEffect, useRef, useState } from 'react';

export interface Countdown {
  /** Whole seconds remaining, never negative. */
  seconds: number;
  /** 1 → just started, 0 → expired. Drives the timer ring. */
  fraction: number;
  expired: boolean;
}

/**
 * Ticks against an absolute server deadline rather than counting down locally,
 * so a laggy tab or a background throttle can never drift out of sync with the
 * authoritative clock.
 */
export function useCountdown(deadline: number | null, totalMs: number): Countdown {
  const [now, setNow] = useState(() => Date.now());
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    if (deadline === null) return;

    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      setNow(Date.now());
      frameRef.current = window.requestAnimationFrame(tick);
    };
    frameRef.current = window.requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      if (frameRef.current !== null) window.cancelAnimationFrame(frameRef.current);
    };
  }, [deadline]);

  if (deadline === null) {
    return { seconds: 0, fraction: 0, expired: true };
  }

  const remaining = Math.max(0, deadline - now);
  return {
    seconds: Math.ceil(remaining / 1000),
    fraction: totalMs > 0 ? Math.min(1, remaining / totalMs) : 0,
    expired: remaining <= 0,
  };
}
